import Vue from "vue"
import router from "@/router"
import _ from "lodash"
import { SESSION_REQUEST_STATUSES } from "@/constants/coaching"
import {
  getGames,
  getCoachUser,
  getCoaches,
  getRecommendedCoaches,
  getFavoriteCoaches,
  getSessionRequests,
  searchCoachesForGame,
  favoriteCoach,
  unfavoriteCoach,
  getCoachByUsername,
  getCoachReviews,
  getCoachSubscriptions,
} from "@/api/coaching"
import { getSessionRequest } from "@/api/user/coaching/student/sessionRequests"
import { fetchCoachUsers } from "./coaching.apis"
import CoachSubscription from "../modules/coaching/CoachSubscription"
import Schedule from "../modules/student/coaching/schedule"
import CoachService from "../modules/coaching/CoachService"
import CoachSearch from "../modules/coaching/CoachSearch"
import CoachWidget from "./widget/widget.module"
import GameplayAnalysis from "./gameplay-analysis/gameplay-analysis.module"
import Games from "./games/games.module"
import { COACHIFY_MAP } from "./coaching.constants"

const state = {
  // Data stores
  games: {},
  gamesByKey: null,
  coachesById: {},
  coachUsersById: {}, // Replica of coachesById, with the user.coach hierarchical format, for legacy support
  coachSubsByCoachId: {},
  recommendedCoaches: [],
  favoriteCoaches: [],
  totalOnlineCoaches: 0,
  activeSessionRequest: null,
  coachingGame: null,
  // Searching related states
  coaches: [],
  taxonomies: {},
  sortOptions: [],
  selectedSearchSorting: null,
  currentCoachesPage: 1,
  totalCoachesPage: 5,
  appliedTaxonomies: {},
  searchKeyWords: null,
  searchResult: "",
  selectedCoach: {},
  selectedCoachReviews: [],
  selectedCoachReviewPage: 1,
  selectedCoachReviewTotalPages: 5,
  selectedService: null,
  searchFilters: [],
  availabilityFilters: ["THIS_WEEK"],
  anyOfflineAvailable: true,
  searchLoading: false,
  featuredCoaches: [],
  selectedCoachSubscriptions: [],
  // aboves are legacy
  featuredCoachesAcrossGames: [],
  nonFeaturedCoachesAcrossGames: [],
}

const getters = {
  getInstaProGames: state => (state.games ? state.games : {}),
  getAvailableInstaProGames: state => {
    const games = {}
    Object.values(state.games).forEach(game => {
      if (game.available) {
        games[game.name] = game
      }
    })
    return games
  },
  coachesById: state => state.coachesById,
  getCoach: state => coachId => state.coachesById[coachId],
  getCoachByUserId: state => coachUserId => {
    return Object.values(state.coachesById)
      .find(coachData => coachData?.user.id === coachUserId)
  },
  getCoachUser: state => coachUserId => state.coachUsersById[coachUserId],
  getCoachUserByCoachId: state => coachId => {
    return Object.values(state.coachUsersById)
      .find(coachUserData => coachUserData?.coach.id === coachId)
  },
  recommendedCoaches: state => state.recommendedCoaches,
  favoriteCoaches: state => state.favoriteCoaches,
  totalOnlineCoaches: state => state.totalOnlineCoaches,
  activeSessionRequest: state => state.activeSessionRequest,
  inSession: state => {
    try {
      return [
        SESSION_REQUEST_STATUSES.ACCEPTED,
        SESSION_REQUEST_STATUSES.IN_SESSION,
      ].includes(state.activeSessionRequest.status)
    } catch (error) {
      return false
    }
  },
  coachingGame: state => state.coachingGame,
  gamesByKey: state => state.gamesByKey,
  taxonomies: state => state.taxonomies,
  coaches: state => state.coaches,
  featuredCoaches: state => state.featuredCoaches,
  sortOptions: state => state.sortOptions,
  selectedSearchSorting: state => state.selectedSearchSorting,
  currentCoachesPage: state => state.currentCoachesPage,
  totalCoachesPage: state => state.totalCoachesPage,
  appliedTaxonomies: state => state.appliedTaxonomies,
  searchKeyWords: state => state.searchKeyWords,
  searchResult: state => state.searchResult,
  selectedCoach: state => state.selectedCoach,
  selectedCoachCoachifyId: state => state.selectedCoach?.id ? COACHIFY_MAP[state.selectedCoach.id] : null,
  selectedCoachReviews: state => state.selectedCoachReviews,
  selectedCoachReviewPage: state => state.selectedCoachReviewPage,
  selectedCoachReviewTotalPages: state => state.selectedCoachReviewTotalPages,
  selectedService: state => state.selectedService,
  searchFilters: state => state.searchFilters,
  availabilityFilters: state => state.availabilityFilters,
  anyOfflineAvailable: state => state.anyOfflineAvailable,
  searchLoading: state => state.searchLoading,
  selectedCoachSubscriptions: state => state.selectedCoachSubscriptions,
  // above are legacy
  featuredCoachesAcrossGames: state => state.featuredCoachesAcrossGames,
  nonFeaturedCoachesAcrossGames: state => state.nonFeaturedCoachesAcrossGames,
  coachSubscriptionsByCoachId: state => coachId => state.coachSubsByCoachId[coachId] || [],
}

const mutations = {
  SET_GAMES: (state, gamesData) => {
    // Convert array into map
    Vue.set(state, "games", {})
    gamesData.forEach(gameData => {
      // "global" is reserved for other hacky uses, so it is not intended to be
      // included as a game here
      if (gameData.name !== "global") {
        Vue.set(state.games, gameData.id, gameData)
      }
    })
  },
  SAVE_COACH: (state, coach) => {
    Vue.set(state.coachesById, coach.id, coach)

    // Format to user.coach from coach.user hierarchy
    let coachData = _.clone(coach)
    let coachUser = _.clone(coachData.user || {})
    delete coachData.user
    coachUser.coach = coachData
    Vue.set(state.coachUsersById, coachUser.id, coachUser)
  },
  SAVE_COACH_USER: (state, coachUser) => {
    Vue.set(state.coachUsersById, coachUser.id, coachUser)

    // Format to coach.user from user.coach hierarchy
    let coachUserData = _.clone(coachUser)
    let coach = _.clone(coachUserData.coach || {})
    delete coachUserData.coach
    coach.user = coachUserData
    Vue.set(state.coachesById, coach.id, coach)
  },
  setRecommendedCoaches: (state, coaches) =>
    (state.recommendedCoaches = coaches),
  setFavoriteCoaches: (state, coaches) => (state.favoriteCoaches = coaches),
  setTotalOnlineCoaches: (state, number) => (state.totalOnlineCoaches = number),
  setActiveSessionRequest: (state, request) =>
    (state.activeSessionRequest = { ...request }),
  setCoachingGame: (state, game) => (state.coachingGame = game),
  setGamesByKey: (state, gamesData) => {
    Vue.set(state, "gamesByKey", {})
    gamesData.forEach(gameData => {
      if (gameData.name !== "global") {
        Vue.set(state.gamesByKey, gameData.name, gameData)
      }
    })
  },
  setTaxonomies: (state, taxonomies) => (state.taxonomies = taxonomies),
  setCoaches: (state, coaches) => (state.coaches = coaches),
  setFeaturedCoaches: (state, featuredCoaches) =>
    (state.featuredCoaches = featuredCoaches),
  setSortOptions: (state, sortOptions) => (state.sortOptions = sortOptions),
  setSelectedSearchSorting: (state, selectedSearchSorting) =>
    (state.selectedSearchSorting = selectedSearchSorting),
  setCurrentCoachesPage: (state, page) => (state.currentCoachesPage = page),
  setTotalCoachesPage: (state, page) => (state.totalCoachesPage = page),
  setAppliedTaxonomies: (state, taxonomies) =>
    (state.appliedTaxonomies = { ...taxonomies }),
  setSearchKeyWords: (state, searchKeyWords) =>
    (state.searchKeyWords = searchKeyWords),
  setSearchResult: (state, searchResult) => (state.searchResult = searchResult),
  setSelectedCoach: (state, selectedCoach) =>
    (state.selectedCoach = { ...selectedCoach }),
  setSelectedCoachReviews: (state, reviews) =>
    (state.selectedCoachReviews = reviews),
  setSelectedCoachReviewPage: (state, page) =>
    (state.selectedCoachReviewPage = page),
  setSelectedCoachReviewTotalPages: (state, page) =>
    (state.selectedCoachReviewTotalPages = page),
  setSearchFilters: (state, searchFilters) =>
    (state.searchFilters = searchFilters),
  setAvailabilityFilters: (state, availabilityFilters) =>
    (state.availabilityFilters = availabilityFilters),
  setAnyOfflineAvailable: (state, anyOfflineAvailable) =>
    (state.anyOfflineAvailable = anyOfflineAvailable),
  setSearchLoading: (state, bol) => (state.searchLoading = bol),
  setSelectedCoachSubscriptions: (state, subscriptions) =>
    (state.selectedCoachSubscriptions = subscriptions),
  // aboves are legacy
  setFeaturedCoachesAcrossGames: (state, coaches) =>
    (state.featuredCoachesAcrossGames = coaches),
  setNonFeaturedCoachesAcrossGames: (state, coaches) =>
    (state.nonFeaturedCoachesAcrossGames = coaches),
  SAVE_COACH_SUBSCRIPTIONS: (state, { coachId, subscriptions }) => {
    Vue.set(state.coachSubsByCoachId, coachId, subscriptions)
  },
}

const actions = {
  resetCoachSearch ({ commit }) {
    commit("setCurrentCoachesPage", 1)
    commit("setTotalCoachesPage", 2)
    commit("setCoaches", [])
    commit("setFeaturedCoaches", [])
    commit("setSortOptions", [])
    commit("setSelectedSearchSorting", null)
    commit("setAppliedTaxonomies", {})
    commit("setSearchKeyWords", null)
    commit("setSearchFilters", [])
    commit("setAvailabilityFilters", ["THIS_WEEK"])
  },
  async loadInstaProGames ({ state, commit }) {
    if (!state.games || !state.gamesByKey) {
      try {
        const games = await getGames()
        commit("SET_GAMES", games)
        commit("setGamesByKey", games)
      } catch (e) {
        console.error("Failed to fetch InstaPRO games.")
      }
    }
  },
  saveCoach ({ commit }, coach) {
    commit("SAVE_COACH", coach)
  },
  saveCoachUser ({ commit }, coachUser) {
    commit("SAVE_COACH_USER", coachUser)
  },
  async loadCoachByUserId ({ state, commit }, coachUserId) {
    if (coachUserId in state.coachUsersById) {
      return
    }

    const response = await getCoachUser(coachUserId)
    const coachUser = response.result
    commit("SAVE_COACH_USER", coachUser)
  },
  async loadRecommendedCoaches ({ commit }, { game, languages }) {
    if (game) {
      const data = await getRecommendedCoaches({ game, languages })
      const coaches = data.result.data
      await commit("setRecommendedCoaches", coaches)
    } else {
      await commit("setRecommendedCoaches", [])
    }
  },
  async loadFavoriteCoaches ({ commit, rootGetters }) {
    const instaProAccessToken = rootGetters["User/Coaching/getInstaProAccessToken"]
    if (!instaProAccessToken) {
      return
    }

    const data = await getFavoriteCoaches({ instaProAccessToken })
    const coaches = data.result.data
    await commit("setFavoriteCoaches", coaches)
  },
  async loadTotalOnlineCoaches ({ commit }) {
    const data = await getCoaches("online")
    commit("setTotalOnlineCoaches", data.result.total)
  },
  async loadActiveSessionRequest ({ commit, dispatch }) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    const data = await getSessionRequests(
      "SYNC",
      "PENDING",
      instaProAccessToken
    )
    if (data) {
      const request = data.result.data[0]
      if (request) {
        commit("setActiveSessionRequest", request)
      }
    }
  },
  async loadCoachingGame ({ commit, rootGetters, dispatch }) {
    let activeGame
    try {
      await dispatch("User/loadUserActiveGames", null, { root: true })
      activeGame = rootGetters["User/currentActiveGame"]

      if (activeGame == null) {
        throw 'User/loadUserActiveGames, Active Game not available'
      }
    } catch (error) {
      const game = router.currentRoute.params.game
      if (game) {
        activeGame = game
      } else {
        activeGame = "leagueoflegends"
      }
    }
    await commit("setCoachingGame", activeGame)
  },
  async getOfflineCoachesWithAvailableSlots ({ rootGetters, state, commit }) {
    commit("setSearchLoading", true)
    const instaProAccessToken =
      rootGetters["User/Coaching/getInstaProAccessToken"]
    const gameId = state.gamesByKey[state.coachingGame].id
    const data = await searchCoachesForGame(
      instaProAccessToken,
      gameId,
      1,
      state.selectedSearchSorting,
      Object.values(state.appliedTaxonomies),
      state.searchKeyWords,
      [...state.searchFilters, "OFFLINE", "HAS_OPEN_SCHEDULE_SLOTS"]
    )
    if (!data || !data.success) return
    if (data.result.data.length) {
      await commit("setAnyOfflineAvailable", true)
    } else {
      await commit("setAnyOfflineAvailable", false)
    }
  },
  async searchFeaturedCoachesForGame ({ rootGetters, state, commit, dispatch }) {
    const instaProAccessToken =
      rootGetters["User/Coaching/getInstaProAccessToken"]
    const gameId = state.gamesByKey[state.coachingGame].id
    const data = await searchCoachesForGame(
      instaProAccessToken,
      gameId,
      null,
      null,
      [],
      null,
      ["FEATURED", ...state.availabilityFilters]
    )
    if (!data || !data.success) return
    // Set coaches
    commit("setFeaturedCoaches", data.result.data)
  },
  async searchCoachesForGame ({ rootGetters, state, commit, dispatch }) {
    commit("setSearchLoading", true)
    await dispatch("getOfflineCoachesWithAvailableSlots")
    commit("setCurrentCoachesPage", 1)

    const instaProAccessToken =
      rootGetters["User/Coaching/getInstaProAccessToken"]
    const gameId = state.gamesByKey[state.coachingGame].id
    const filters = state.anyOfflineAvailable ? [
      ...state.searchFilters,
      "ONLINE_OR_HAS_OPEN_SCHEDULE_SLOTS",
      "NOT_FEATURED",
    ] : [...state.searchFilters, "NOT_FEATURED"]
    const data = await searchCoachesForGame(
      instaProAccessToken,
      gameId,
      state.currentCoachesPage,
      state.selectedSearchSorting,
      Object.values(state.appliedTaxonomies),
      state.searchKeyWords,
      filters
    )

    if (!data || !data.success) return
    // Set coaches
    commit("setCoaches", data.result.data)
    commit("setTotalCoachesPage", data.result.last_page)
    commit("setSearchResult", state.searchKeyWords)
    const taxonomies = {}
    // Set taxonomy and sort options
    for (let taxonomy of data.result.taxonomies) {
      if (!taxonomies[taxonomy.taxonomy_type]) {
        taxonomies[taxonomy.taxonomy_type] = []
      }
      taxonomies[taxonomy.taxonomy_type].push(taxonomy)
    }
    commit("setTaxonomies", taxonomies)
    if (!state.sortOptions.length) {
      commit("setSortOptions", data.result.sort_options)
      commit("setSelectedSearchSorting", data.result.sort_options[0])
    }
    commit("setSearchLoading", false)
  },
  async searchMoreCoachesForGame ({ rootGetters, state, commit }) {
    commit("setSearchLoading", true)
    const instaProAccessToken =
      rootGetters["User/Coaching/getInstaProAccessToken"]
    const gameId = state.gamesByKey[state.coachingGame].id

    const data = await searchCoachesForGame(
      instaProAccessToken,
      gameId,
      state.currentCoachesPage,
      state.selectedSearchSorting,
      Object.values(state.appliedTaxonomies),
      state.searchKeyWords,
      state.anyOfflineAvailable
        ? [
          ...state.searchFilters,
          "ONLINE_OR_HAS_OPEN_SCHEDULE_SLOTS",
          "NOT_FEATURED",
        ]
        : [...state.searchFilters, "NOT_FEATURED"]
    )
    commit("setCoaches", data.result.data)
    commit("setSearchLoading", false)
  },
  async favoriteCoach ({ rootGetters }, coachId) {
    const instaProAccessToken =
      rootGetters["User/Coaching/getInstaProAccessToken"]
    await favoriteCoach(coachId, instaProAccessToken)
  },
  async unfavoriteCoach ({ rootGetters }, coachId) {
    const instaProAccessToken =
      rootGetters["User/Coaching/getInstaProAccessToken"]
    await unfavoriteCoach(coachId, instaProAccessToken)
  },
  async getCoachByUsername ({ commit, rootGetters }, username) {
    const instaProAccessToken =
      rootGetters["User/Coaching/getInstaProAccessToken"]
    const data = await getCoachByUsername(username, instaProAccessToken)
    commit("setSelectedCoach", data.result)
  },
  async getCoachReviews ({ commit, rootGetters, state }) {
    const instaProAccessToken =
      rootGetters["User/Coaching/getInstaProAccessToken"]
    const data = await getCoachReviews(
      state.selectedCoach.id,
      instaProAccessToken,
      state.selectedCoachReviewPage
    )
    commit("setSelectedCoachReviews", data.result)
    commit("setSelectedCoachReviewTotalPages", data.pagination.last_page)
  },
  async getMoreCoachReviews ({ commit, rootGetters, state }) {
    const instaProAccessToken =
      rootGetters["User/Coaching/getInstaProAccessToken"]
    const data = await getCoachReviews(
      state.selectedCoach.id,
      instaProAccessToken,
      state.selectedCoachReviewPage + 1
    )
    commit("setSelectedCoachReviews", [
      ...state.selectedCoachReviews,
      ...data.result,
    ])
    commit("setSelectedCoachReviewPage", data.pagination.current_page)
  },
  async getCoachSubscriptions ({ commit, state }) {
    const coachId = state.selectedCoach.coach.id
    const data = await getCoachSubscriptions(coachId)
    commit("setSelectedCoachSubscriptions", data)
    commit("SAVE_COACH_SUBSCRIPTIONS", {
      coachId: coachId,
      subscriptions: data,
    })
  },
  async getActiveSessionRequestById ({ commit, dispatch }, requestId) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    const { success, result } = await getSessionRequest(instaProAccessToken, requestId)
    if (success && !result.async_param) {
      commit("setActiveSessionRequest", result)
    }
  },
  // aboves are legacy
  async fetchFeaturedCoachesAcrossGames ({ commit }) {
    const filters = ["FEATURED"]
    const data = await fetchCoachUsers({ filters })
    const coaches = data?.result?.data
    if (Array.isArray(coaches)) {
      commit("setFeaturedCoachesAcrossGames", coaches)
    }
  },
  async fetchNonFeaturedCoachesAcrossGames ({ commit }) {
    const filters = ["NOT_FEATURED"]
    const data = await fetchCoachUsers({ filters })
    const coaches = data?.result?.data
    if (Array.isArray(coaches)) {
      commit("setNonFeaturedCoachesAcrossGames", coaches)
    }
  },
  async loadCoachSubscriptions ({ commit, state }, coachId) {
    if (coachId in state.coachSubsByCoachId) {
      return
    }
    const data = await getCoachSubscriptions(coachId)
    commit("SAVE_COACH_SUBSCRIPTIONS", {
      coachId: coachId,
      subscriptions: data,
    })
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
  modules: {
    CoachSubscription,
    Schedule,
    CoachService,
    CoachSearch,
    CoachWidget,
    GameplayAnalysis,
    Games,
  },
}



// WEBPACK FOOTER //
// ./src/store/coaching/coaching.module.js